import CodeBlock from '../CodeBlock';

const ENDPOINTS: Array<[string, string, string, string]> = [
  ['POST', '/api/v1/feeds', 'feeds:write', '201 · 402 · 422'],
  ['GET', '/api/v1/feeds', 'feeds:read', '200'],
  ['GET', '/api/v1/feeds/{feed_id}', 'feeds:read', '200 · 404'],
  ['PATCH', '/api/v1/feeds/{feed_id}', 'feeds:write', '200 · 404 · 422'],
  ['DELETE', '/api/v1/feeds/{feed_id}', 'feeds:write', '204 · 404'],
  ['POST', '/api/v1/feeds/{feed_id}/rotate-secret', 'feeds:write', '200 · 404 · 422'],
  ['POST', '/api/v1/feeds/{feed_id}/screens', 'feeds:write', '201 · 402 · 404 · 422'],
  ['GET', '/api/v1/feeds/{feed_id}/screens', 'feeds:read', '200 · 404'],
  ['GET', '/api/v1/feeds/{feed_id}/screens/{screen_id}', 'feeds:read', '200 · 404'],
  ['POST', '/api/v1/feeds/{feed_id}/emissions', 'feeds:write', '201 · 200 · 404 · 409 · 422 · 500'],
  ['GET', '/api/v1/feeds/{feed_id}/emissions', 'feeds:read', '200 · 404'],
  ['GET', '/api/v1/feeds/{feed_id}/emissions/{emission_id}', 'feeds:read', '200 · 404'],
  ['POST', '/api/v1/feeds/{feed_id}/run', 'feeds:write', '404 · 501'],
];

const IDEMPOTENCY: Array<[string, string, string]> = [
  ['none', 'dry_run=true', '201 — row reserved, nothing sent, preview returned'],
  ['none', 'dry_run=false', '201 — row inserted, delivered, preview returned'],
  ['dry_run=true', 'dry_run=false', '201 — upgrade: the reserved row is claimed atomically, then delivered'],
  ['dry_run=true', 'dry_run=true', '200 replay'],
  ['dry_run=false', 'anything', '200 replay'],
  ['another feed', 'anything', '409 — emission_id already recorded for another feed'],
];

const HEADERS: Array<[string, string]> = [
  ['Content-Type', 'application/json'],
  ['X-PropAlgos-Key-Id', 'pk_live_… — names the Vault secret, never the secret'],
  ['X-PropAlgos-Timestamp', 'unix seconds; receivers reject ±300s drift'],
  ['X-PropAlgos-Signature', 'v1=hmac-sha256:<lowercase hex>'],
  ['Idempotency-Key', 'the emission_id — dedupe on it'],
];

export default function DocsFeedsModule() {
  return (
    <section data-docs-section="feeds" className="text-left mb-16">
      <h2 className="text-2xl font-bold text-white mb-2 group">
        <a href="#feeds" className="hover:text-sky-400 transition-colors">Signal Feeds</a>
        <span className="ml-2 opacity-0 group-hover:opacity-100 text-gray-600 transition-opacity">#</span>
      </h2>
      <p className="text-gray-400 mb-3">
        A feed is the delivery half of a strategy: an execution endpoint, the params a
        cycle ranks with, and an HMAC key the platform signs every emission with. You get
        back <code className="text-gray-300">key_id</code> — the <em>name</em> of the secret,
        which is what your receiver verifies against. The secret itself lives in Vault and
        appears in exactly one response body, <code className="text-gray-300">rotate-secret</code>,
        which replaces it. Nothing reads an existing secret back.
      </p>
      <div className="p-3 bg-gray-900/50 border border-gray-800 rounded-lg mb-6 text-sm text-gray-400">
        <strong className="text-gray-300">The invariant:</strong> at most one delivery per{' '}
        <code className="text-sky-400">emission_id</code>, held by the database rather than by
        a read-then-write check. Concurrent POSTs of one id produce exactly one send and one
        row; the losers send nothing and get a <code className="text-gray-300">200</code> replay.
      </div>

      {/* Endpoints */}
      <div id="feeds-endpoints" data-docs-section="feeds-endpoints" className="mb-10">
        <h3 className="text-lg font-semibold text-white mb-3">Endpoints</h3>
        <div className="overflow-x-auto">
          <table className="text-sm text-left w-full">
            <thead>
              <tr className="border-b border-gray-800">
                <th className="py-2 pr-4 text-gray-400 font-medium">Method</th>
                <th className="py-2 pr-4 text-gray-400 font-medium">Path</th>
                <th className="py-2 pr-4 text-gray-400 font-medium">Scope</th>
                <th className="py-2 text-gray-400 font-medium">Status</th>
              </tr>
            </thead>
            <tbody className="text-gray-300">
              {ENDPOINTS.map(([method, path, scope, status]) => (
                <tr key={`${method} ${path}`} className="border-b border-gray-800/50 last:border-0">
                  <td className="py-1.5 pr-4 font-mono text-amber-400">{method}</td>
                  <td className="py-1.5 pr-4 font-mono text-sky-400">{path}</td>
                  <td className="py-1.5 pr-4 text-gray-500 font-mono">{scope}</td>
                  <td className="py-1.5 text-gray-400 font-mono">{status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-gray-400 text-sm mt-3">
          Route-specific codes only. <code className="text-gray-300">401</code>,{' '}
          <code className="text-gray-300">403</code> and <code className="text-gray-300">429</code>{' '}
          come from the shared auth dependency and apply to <em>every</em> row — see the auth
          note in GPU Sweeps above. On the emission path,{' '}
          <code className="text-gray-300">404</code> is feed ownership,{' '}
          <code className="text-gray-300">500</code> is a feed whose Vault secret is gone
          (nothing signable, nothing a retry fixes).
        </p>
        <p className="text-gray-400 text-sm mt-3">
          <code className="text-gray-300">402</code> is the prepaid balance talking: feed
          creation needs the same $5 minimum every launch does, and a screen is refused when
          the per-screen fee can&apos;t be covered. Per-screen and per-emission fees are $0
          today, so in practice only the create gate fires — both go live the moment
          they&apos;re priced. Emissions never answer{' '}
          <code className="text-gray-300">402</code>: they are metered <em>after</em> delivery,
          so a shortfall there is recorded as unbilled rather than refused.
        </p>
        <p className="text-gray-400 text-sm mt-3">
          <code className="text-gray-300">POST /run</code> answers{' '}
          <code className="text-gray-300">501</code> today — server-side cycle runs need the
          S3 close-panel loader that lands with the jobs plan. A{' '}
          <code className="text-gray-300">501</code> means nothing was sent: don&apos;t retry it,
          and don&apos;t treat it as &ldquo;the emission might have gone out.&rdquo; Until then,
          run the cycle where the data is and POST the result to{' '}
          <code className="text-gray-300">/emissions</code> — the same signing and audit path.
        </p>
        <p className="text-gray-400 text-sm mt-3">
          <code className="text-gray-300">POST /rotate-secret</code> is the one row that returns a
          secret, and it returns it <strong className="text-gray-300">once</strong> — the same
          show-once rule as <code className="text-gray-300">POST /api/v1/keys</code>. It is in that{' '}
          <code className="text-gray-300">200</code> body and nowhere else: not on the row, not in
          a log, not on any later read. POST <code className="text-gray-300">{'{}'}</code> and the
          platform mints the pair; POST{' '}
          <code className="text-gray-300">{'{key_id, secret}'}</code> and it adopts a pair your
          receiver minted, so the secret never travels to you at all (either half works alone; a
          supplied secret is 32–128 printable-ASCII characters with no spaces, anything else a{' '}
          <code className="text-gray-300">422</code> that writes nothing). The old secret stops
          verifying immediately on <em>every</em> replica — signing reads Vault directly rather
          than through its read cache — but is not destroyed: Vault&apos;s KV v2 keeps it as an
          earlier version, so a broken cutover is recoverable by an operator. Concurrent
          rotations are serialized by a row lock but not merged: last writer wins, so
          don&apos;t race this endpoint. Rotation works on a <em>disabled</em> feed — the kill
          switch governs delivery, not custody.
        </p>
        <p className="text-gray-400 text-sm mt-3">
          Two things about the cutover window that aren&apos;t obvious. An emission already{' '}
          <em>in flight</em> when you rotate still delivers and still verifies — under the{' '}
          <em>old</em> key: its secret was read before the rotation, and the request is
          self-consistent, since{' '}
          <code className="text-gray-300">X-PropAlgos-Key-Id</code> names the key that actually
          signed it. And an emission whose delivery failed during the gap is{' '}
          <strong className="text-gray-300">not</strong> retryable under the same{' '}
          <code className="text-gray-300">emission_id</code> — that id is already reserved, so
          re-POSTing it returns a <code className="text-gray-300">200</code> replay and sends
          nothing, forever. Mint a new id for the retry.
        </p>
        <CodeBlock
          language="bash"
          code={`$ propalgos feeds rotate-secret <feed_id> --json
{"feed_id": "fd_…", "key_id": "pk_live_…", "secret": "…", "rotated_at": "…"}

# or pair with a secret your receiver minted — passed by env var NAME, never argv
$ export RECEIVER_HMAC=...
$ propalgos feeds rotate-secret <feed_id> \\
    --key-id pk_live_theirs0000000000 --secret-from-env RECEIVER_HMAC --json`}
        />
      </div>

      {/* Create */}
      <div id="feeds-create" data-docs-section="feeds-create" className="mb-10">
        <h3 className="text-lg font-semibold text-white mb-2">Create a feed</h3>
        <CodeBlock
          language="bash"
          code={`$ propalgos feeds create --strategy statarb-1h --name statarb-demo \\
    --execution-url https://exec.example.com/signals \\
    --param z_window=48 --param entry_z=2.0 --param max_pairs=10 \\
    --param weight_per_leg=0.05 --cadence 1h --json`}
        />
        <p className="text-gray-400 text-sm mb-3">
          <code className="text-sky-400">--param</code> values are typed, not strings:{' '}
          <code className="text-gray-300">48</code> is an int,{' '}
          <code className="text-gray-300">2.0</code> a float,{' '}
          <code className="text-gray-300">true</code>/<code className="text-gray-300">false</code>{' '}
          booleans, anything else stays a string. A feed whose params were all strings fails at
          cycle time, hours after the command that caused it.
        </p>
        <p className="text-gray-400 text-sm">
          <code className="text-gray-300">execution_url</code> must be https and survive the
          SSRF guard, which runs at create <em>and</em> again immediately before every
          delivery — the send dials the IP the second check pinned, so a DNS rebind between the
          two closes on nothing. To stop delivering without losing the audit trail, disable
          rather than delete: <code className="text-gray-300">propalgos feeds disable &lt;feed_id&gt;</code>.
          Deleting cascades the feed&apos;s screens and emissions.
        </p>
      </div>

      {/* Emissions */}
      <div id="feeds-emissions" data-docs-section="feeds-emissions" className="mb-10">
        <h3 className="text-lg font-semibold text-white mb-2">Dry run, then send</h3>
        <p className="text-gray-400 text-sm mb-3">
          A dry run signs the payload and returns it without delivering. It reserves the{' '}
          <code className="text-gray-300">emission_id</code> but does not burn it — the real
          POST upgrades that same row in place. Without that, previewing an emission would
          permanently prevent sending it.
        </p>
        <CodeBlock
          code={`preview = client.upload_emission(
    feed_id, description.to_dict(), dry_run=True, screen_id=screen_id
)

request = preview["request_preview"]   # null on an idempotent replay — check first
print(request["url"], request["headers"])
print(request["body"])   # the exact signed bytes; key order is part of the contract

sent = client.upload_emission(feed_id, description.to_dict(), screen_id=screen_id)
print(sent["delivered"], sent["response_status"], len(sent["attempts"]))`}
        />
        <div className="overflow-x-auto">
          <table className="text-sm text-left w-full">
            <thead>
              <tr className="border-b border-gray-800">
                <th className="py-2 pr-4 text-gray-400 font-medium">Existing row</th>
                <th className="py-2 pr-4 text-gray-400 font-medium">Request</th>
                <th className="py-2 text-gray-400 font-medium">Result</th>
              </tr>
            </thead>
            <tbody className="text-gray-300">
              {IDEMPOTENCY.map(([prior, request, result]) => (
                <tr key={`${prior}-${request}`} className="border-b border-gray-800/50 last:border-0">
                  <td className="py-1.5 pr-4 font-mono text-gray-500">{prior}</td>
                  <td className="py-1.5 pr-4 font-mono text-sky-400">{request}</td>
                  <td className="py-1.5 text-gray-400">{result}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-gray-400 text-sm mt-3">
          A replay returns the stored audit row plus{' '}
          <code className="text-gray-300">idempotent_replay: true</code> and{' '}
          <code className="text-gray-300">request_preview: null</code>. The null is deliberate:
          re-signing on a replay would show a signature with a fresh timestamp that was never
          sent. A disabled feed refuses real emissions with{' '}
          <code className="text-gray-300">409</code> but still accepts dry runs — previewing
          sends nothing, so disabling stays honest.
        </p>
      </div>

      {/* HMAC */}
      <div id="feeds-hmac" data-docs-section="feeds-hmac" className="mb-10">
        <h3 className="text-lg font-semibold text-white mb-3">What your endpoint receives</h3>
        <div className="overflow-x-auto mb-3">
          <table className="text-sm text-left w-full">
            <thead>
              <tr className="border-b border-gray-800">
                <th className="py-2 pr-4 text-gray-400 font-medium">Header</th>
                <th className="py-2 text-gray-400 font-medium">Value</th>
              </tr>
            </thead>
            <tbody className="text-gray-300">
              {HEADERS.map(([name, value]) => (
                <tr key={name} className="border-b border-gray-800/50 last:border-0">
                  <td className="py-1.5 pr-4 font-mono text-sky-400">{name}</td>
                  <td className="py-1.5 text-gray-400 font-mono text-xs">{value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <CodeBlock
          code={`import hashlib, hmac

# verify over the RAW request body, before parsing it
message = f"{timestamp}.".encode("utf-8") + raw_body
expected = hmac.new(secret.encode("utf-8"), message, hashlib.sha256).hexdigest()
assert hmac.compare_digest(signature_header, "v1=hmac-sha256:" + expected)`}
        />
        <p className="text-gray-400 text-sm">
          Delivery is 3 attempts, 1s then 2s backoff, 5s per-attempt timeout. Retries happen
          on transport errors, <code className="text-gray-300">429</code> and{' '}
          <code className="text-gray-300">5xx</code> only — every other{' '}
          <code className="text-gray-300">4xx</code> is a contract failure and retrying it just
          burns budget. All three attempts carry the <em>same</em> timestamp, signature and
          idempotency key, so a retry after a response you already processed is a duplicate to
          drop, not a second execution. Redirects are disabled and at most 64KB of your
          response is read.
        </p>
      </div>

      {/* Audit */}
      <div id="feeds-audit" data-docs-section="feeds-audit" className="mb-8">
        <h3 className="text-lg font-semibold text-white mb-2">Audit provenance</h3>
        <p className="text-gray-400 text-sm mb-3">
          Every emission row records <code className="text-gray-300">dry_run</code>,{' '}
          <code className="text-gray-300">delivered</code>,{' '}
          <code className="text-gray-300">attempts</code>,{' '}
          <code className="text-gray-300">response_status</code> and{' '}
          <code className="text-gray-300">payload_sha256</code>. Fetching a single row adds{' '}
          <code className="text-gray-300">payload_json</code> and{' '}
          <code className="text-gray-300">response_body</code>, which the list omits — a page
          of 50 would be mostly payload bytes.
        </p>
        <CodeBlock
          language="bash"
          code={`$ propalgos feeds emissions <feed_id> --limit 20 --json
$ propalgos feeds emission <feed_id> <emission_id>
emission_id: em_01M0...
delivered: True
dry_run: False
response_status: 200
attempts: 1
payload_sha256: 3f7a...
sha256(payload_json) matches: yes`}
        />
        <CodeBlock
          code={`import hashlib

row = client.get_emission(feed_id, emission_id)
digest = hashlib.sha256(row["payload_json"].encode("utf-8")).hexdigest()
assert digest == row["payload_sha256"]`}
        />
        <p className="text-gray-400 text-sm">
          Hash <code className="text-gray-300">payload_json</code>, never{' '}
          <code className="text-gray-300">payload</code>. The latter came back through JSONB
          with its key order normalised, so re-serialising it will not reproduce the signed
          bytes and the check fails on a payload that was perfectly fine.
        </p>
      </div>
    </section>
  );
}
